import React, {useMemo, useState, useEffect, useCallback} from 'react';
import { useSelector } from 'react-redux';
import CustomSwitch from '../../../../../../../components/form/switch/switch';
import CustomSelect from '../../../../../../../components/form/customSelect/customSelect';
import Button from '../../../../../../../components/form/button/button';
import DCavg from './dcavg/dcavg';
import MTTFd from './mttfd/mttfd';
import CCF from './ccf/ccf';
import HSR from './hsr/hsr';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CustomToggleButton from '../../../../../../../components/toggle-button/toggle-button';
import useFunctionalSafety from '../../../../../../../hooks/useFunctionalSafety';
import { FUNCTIONAL_SAFETY_CATEGORY, getMTTFCategory, hsrToDcEquivalent, getDcavgCategory, MTTF_CATEGORY, getCcfResult, checkMplaCriteria, calculateMPLA, getMplaCategoryImage, compareMPLaAndMPLr } from '../../../../../../../utils/constants';

 const categoryEnum = FUNCTIONAL_SAFETY_CATEGORY;

// Helper function to get criteria descriptions
const getCriteriaDescription = (category, mttfCategory, dcavgCategory, ccfResult, mplaCheck) => {
  const descriptions = {
    mttf: '',
    dc: '',
    ccf: '',
  };

  if (category === FUNCTIONAL_SAFETY_CATEGORY.CATEGORY_B) {
    if (!mplaCheck.details.mttf) {
      descriptions.mttf = `Required: MTTFd LOW to MEDIUM. Current: ${mttfCategory || 'N/A'}`;
    }
  } else if (category === FUNCTIONAL_SAFETY_CATEGORY.CATEGORY_1) {
    if (!mplaCheck.details.mttf) {
      descriptions.mttf = `Required: MTTFd HIGH. Current: ${mttfCategory || 'N/A'}`;
    }
  } else if (category === FUNCTIONAL_SAFETY_CATEGORY.CATEGORY_2) {
    if (!mplaCheck.details.mttf) {
      descriptions.mttf = `Required: MTTFd >= LOW. Current: ${mttfCategory || 'N/A'}`;
    }
    if (mplaCheck.applicable.dc && !mplaCheck.details.dc) {
      descriptions.dc = `Required: DCavg >= LOW. Current: ${dcavgCategory || 'N/A'}`;
    }
    if (mplaCheck.applicable.ccf && !mplaCheck.details.ccf) {
      descriptions.ccf = `Required: CCF PASS (score >= 65). Current: ${ccfResult || 'N/A'}`;
    }
  } else if (category === FUNCTIONAL_SAFETY_CATEGORY.CATEGORY_2M) {
    if (!mplaCheck.details.mttf) {
      descriptions.mttf = `Required: MTTFd HIGH. Current: ${mttfCategory || 'N/A'}`;
    }
    if (mplaCheck.applicable.dc && !mplaCheck.details.dc) {
      descriptions.dc = `Required: DCavg >= LOW. Current: ${dcavgCategory || 'N/A'}`;
    }
    if (mplaCheck.applicable.ccf && !mplaCheck.details.ccf) {
      descriptions.ccf = `Required: CCF PASS (score >= 65). Current: ${ccfResult || 'N/A'}`;
    }
  } else if (category === FUNCTIONAL_SAFETY_CATEGORY.CATEGORY_3) {
    if (!mplaCheck.details.mttf) {
      descriptions.mttf = `Required: MTTFd LOW to HIGH. Current: ${mttfCategory || 'N/A'}`;
    }
    if (mplaCheck.applicable.dc && !mplaCheck.details.dc) {
      descriptions.dc = `Required: DCavg >= LOW. Current: ${dcavgCategory || 'N/A'}`;
    }
    if (mplaCheck.applicable.ccf && !mplaCheck.details.ccf) {
      descriptions.ccf = `Required: CCF PASS (score >= 65). Current: ${ccfResult || 'N/A'}`;
    }
  } else if (category === FUNCTIONAL_SAFETY_CATEGORY.CATEGORY_4) {
    if (!mplaCheck.details.mttf) {
      descriptions.mttf = `Required: MTTFd HIGH. Current: ${mttfCategory || 'N/A'}`;
    }
    if (mplaCheck.applicable.dc && !mplaCheck.details.dc) {
      descriptions.dc = `Required: DCavg HIGH. Current: ${dcavgCategory || 'N/A'}`;
    }
    if (mplaCheck.applicable.ccf && !mplaCheck.details.ccf) {
      descriptions.ccf = `Required: CCF PASS (score >= 65). Current: ${ccfResult || 'N/A'}`;
    }
  }

  return descriptions;
};

const categories = Object.values(categoryEnum).map(item => {
  return {
    label: item,
    value: item,
  };
});

const showDcAvg = [categoryEnum.CATEGORY_2,categoryEnum.CATEGORY_3, categoryEnum.CATEGORY_4, categoryEnum.CATEGORY_2M];
const showHsr = [categoryEnum.CATEGORY_1,categoryEnum.CATEGORY_B, categoryEnum.CATEGORY_2M];
const showCcf = [categoryEnum.CATEGORY_2,categoryEnum.CATEGORY_2M, categoryEnum.CATEGORY_3, categoryEnum.CATEGORY_4];


const EnhancedFunctionalSafety = ({ taskId, mplr, onScoresChange }) => {
  
  const {
    assessment,
    formData,
    isLoading,
    error,
    handleFormChange,
    openModal,
    closeModal,
    upsertAssessment,
    dcavgAssessments,
    ccfAssessments,
    hsrAssessments,
    groupedMTTFData,
    ccfOptions,
    hsrOptions,
    createMTTF,
    updateMTTF,
    deleteMTTF,
    createDCavg,
    updateDCavg,
    deleteDCavg,
    createCCF,
    deleteCCF,
    createHSR,
    deleteHSR,
    isCreating,
    isUpdating,
    isDeleting,
  } = useFunctionalSafety(taskId);

 
  const [details, setDetails] = useState({
    hydraulicSystem: formData.hydraulic_system || false,
    category: formData.category || '',
    assessment: {
      input: groupedMTTFData?.input || [],
      logic: groupedMTTFData?.logic || [],
      output: groupedMTTFData?.output || [],
      ccf: ccfAssessments || [],
      hsr: hsrAssessments || [],
    },
    hsrMethod: formData.hsr_method || false,
    showDcAvg: formData.show_dcavg || false,
    showCcf: formData.show_ccf || false,
  });

  const [hydraulicError, setHydraulicError] = useState('');
  const [categoryError, setCategoryError] = useState('');

  


  useEffect(() => {
   
    const newDetails = {
      hydraulicSystem: formData.hydraulic_system || false,
      category: formData.category || '',
      assessment: {
        input: groupedMTTFData?.input || [],
        logic: groupedMTTFData?.logic || [],
        output: groupedMTTFData?.output || [],
        ccf: ccfAssessments || [],
        hsr: hsrAssessments || [],
      },
      hsrMethod: formData.hsr_method || false,
      showDcAvg: formData.show_dcavg || false,
      showCcf: formData.show_ccf || false,
    };
    
    setDetails(newDetails);
  }, [formData, groupedMTTFData, ccfAssessments, hsrAssessments]);

 
  const [mtdfModal, setMtdfModal] = useState(false);
  const [dcavgModal, setDcavgModal] = useState(false);
  const [ccfModal, setccfModal] = useState(false);
  const [hsrModal, sethsrModal] = useState(false);

  const handleChange = (name, value) => {
    
    if (name === 'hydraulicSystem' && !value) {
      if (details?.category === categoryEnum.CATEGORY_2M) {
        setHydraulicError('Change the category first: Category 2M is not allowed for non-hydraulic.');
        return;
      }
      if (details?.hsrMethod) {
        setHydraulicError('Turn off HSR method first, then update hydraulic.');
        return;
      }
    }

    // Validate category change when HSR method is enabled
    if (name === 'category') {
      if (details?.hsrMethod && !showHsr.includes(value)) {
        setCategoryError('First turn off HSR method to change the category.');
        return;
      }
      setCategoryError('');
    }

    // Clear category error when HSR method is turned off
    if (name === 'hsrMethod' && !value) {
      setCategoryError('');
    }
   
    if (name === 'hydraulicSystem' && value) {
      setHydraulicError('');
    }
    if (name === 'category' || name === 'hsrMethod') {
      setHydraulicError('');
    }

    setDetails(prev => ({
      ...prev,
      [name]: value,
    }));

    
    const fieldMapping = {
      hydraulicSystem: 'hydraulic_system',
      category: 'category',
      hsrMethod: 'hsr_method',
      showDcAvg: 'show_dcavg',
      showCcf: 'show_ccf',
    };

    const apiFieldName = fieldMapping[name];
    if (apiFieldName) {
      handleFormChange(apiFieldName, value);
    }
  };

  const filteredCategory = useMemo(() => {
    return details?.hydraulicSystem
      ? categories
      : categories?.filter(item => item?.value !== categoryEnum.CATEGORY_2M);
  }, [details?.hydraulicSystem]);

  // Get stats from separate stats state
  const stats = useSelector(state => state.persistedReducer.functionalSafetyReducer.stats);

  // Calculate scores for display
  const scores = useMemo(() => {
    if (!assessment || !stats) return null;

    const mttfScore = stats.total_mttf_score || 0;
    const mttfCategory = getMTTFCategory(mttfScore);
    
    let dcavgScore = 0;
    let dcavgCategory = null;
    let hsrScore = 0;
    let hsrEquivalent = null;
    
    if (assessment.hsr_method) {
      // If HSR method is enabled, use HSR score and convert to DCavg equivalent
      hsrScore = stats.total_hsr_score || 0;
      hsrEquivalent = hsrToDcEquivalent(hsrScore);
      dcavgScore = hsrEquivalent;
      dcavgCategory = getDcavgCategory(hsrEquivalent);
    } else {
      // Otherwise use DCavg score
      // Round to 2 decimal places to avoid floating-point precision issues
      dcavgScore = Math.round(((stats.total_dcavg_score || 0) * 100) * 100) / 100; // Convert from decimal to percentage
      dcavgCategory = getDcavgCategory(dcavgScore);
    }
    
    const ccfScore = stats.total_ccf_score || 0;
    const ccfResult = getCcfResult(ccfScore);

    const ccfNotApplicableByType = {
      input: Boolean(assessment.ccf_input_not_applicable),
      logic: Boolean(assessment.ccf_logic_not_applicable),
      output: Boolean(assessment.ccf_output_not_applicable),
    };

    // Calculate per-section CCF scores
    let ccfScoresByType = {
      input: { score: 0, result: 'N/A', hasAssessments: false, isRequired: false, isMissing: false, isNotApplicable: ccfNotApplicableByType.input },
      logic: { score: 0, result: 'N/A', hasAssessments: false, isRequired: false, isMissing: false, isNotApplicable: ccfNotApplicableByType.logic },
      output: { score: 0, result: 'N/A', hasAssessments: false, isRequired: false, isMissing: false, isNotApplicable: ccfNotApplicableByType.output },
    };

    // Determine which sections are required based on category
    if (assessment.category) {
      if (assessment.category === FUNCTIONAL_SAFETY_CATEGORY.CATEGORY_3 || assessment.category === FUNCTIONAL_SAFETY_CATEGORY.CATEGORY_4) {
        ccfScoresByType.input.isRequired = true;
      }
      ccfScoresByType.logic.isRequired = true;
      ccfScoresByType.output.isRequired = true;
    }

    if (ccfAssessments && ccfOptions) {
      ccfAssessments.forEach(a => {
        if (a.ccf_type && a.ccf_type !== 'not_applicable' && a.ccf_option) {
          const option = ccfOptions.find(opt => opt.id === a.ccf_option);
          if (option && ccfScoresByType[a.ccf_type]) {
            ccfScoresByType[a.ccf_type].score += (option?.score || 0);
            ccfScoresByType[a.ccf_type].hasAssessments = true;
          }
        }
      });

      Object.keys(ccfScoresByType).forEach(type => {
        const section = ccfScoresByType[type];
        if (section.isNotApplicable) {
          section.result = 'N/A';
          section.isMissing = false;
        } else if (section.hasAssessments) {
          section.result = getCcfResult(section.score);
        } else if (section.isRequired) {
          section.result = 'FAILED';
          section.isMissing = true;
        }
      });
    } else {
      Object.keys(ccfScoresByType).forEach(type => {
        const section = ccfScoresByType[type];
        if (section.isNotApplicable) {
          section.result = 'N/A';
          section.isMissing = false;
        } else if (section.isRequired) {
          section.result = 'FAILED';
          section.isMissing = true;
        }
      });
    }

    let finalCcfResult = ccfResult;
    let finalCcfScore = ccfScore;
    const hasMissingRequiredSection = Object.values(ccfScoresByType).some(section => section.isMissing);
    const allCcfNotApplicable = Object.values(ccfScoresByType).every(section => section.isNotApplicable);
    if (allCcfNotApplicable) {
      finalCcfResult = 'PASS';
      finalCcfScore = 65;
    } else if (hasMissingRequiredSection) {
      finalCcfResult = 'FAILED';
      finalCcfScore = 0;
    }

    // Check MPLA criteria if category is selected
    let mplaCheck = null;
    if (assessment.category) {
      mplaCheck = checkMplaCriteria(
        mttfCategory,
        dcavgCategory,
        finalCcfResult,
        assessment.category
      );
    }

    // Calculate MPLa (Machine Performance Level Achieved) if category is selected
    let mpla = null;
    if (assessment.category) {
      mpla = calculateMPLA(
        assessment.category,
        dcavgCategory,
        mttfCategory,
        assessment.hsr_method,
        hsrScore
      );
    }

    // Compare MPLa with MPLr to determine pass/fail
    // Performance Level hierarchy: e (worst) > d > c > b > a > QM (best)
    // System passes if MPLa >= MPLr (MPLa is better than or equal to MPLr)
    // let mplPassFail = null;
    // if (mpla && mplr && mpla !== "Not covered") {
    //   const plHierarchy = {
    //     'e': 6, 'd': 5, 'c': 4, 'b': 3, 'a': 2, 'QM': 1
    //   };
    //   const mplaValue = plHierarchy[mpla] || 0;
    //   const mplrValue = plHierarchy[mplr] || 0;
    //   // MPLa >= MPLr means pass (higher number = worse, so we want MPLa number <= MPLr number)
    //   // Actually, wait - if MPLa = 'c' (4) and MPLr = 'd' (5), then MPLa is better, so it should pass
    //   // So pass if mplaValue <= mplrValue (MPLa is better or equal)
    //   mplPassFail = mplaValue <= mplrValue ? 'PASS' : 'FAIL';
    // }

    const mplPassFail = compareMPLaAndMPLr(mpla, mplr);

    return {
      mttf: {
        score: mttfScore,
        category: mttfCategory,
      },
      dcavg: {
        score: dcavgScore,
        category: dcavgCategory,
        isFromHSR: assessment.hsr_method,
        hsrScore: assessment.hsr_method ? hsrScore : null,
      },
      ccf: {
        score: finalCcfScore,
        result: finalCcfResult,
        byType: ccfScoresByType,
      },
      mpla: mplaCheck,
      mplaValue: mpla, // The calculated MPLa (a, b, c, d, e, or "Not covered")
      mplr: mplr, // MPLr from props
      mplPassFail: mplPassFail, // PASS or FAIL based on comparison
    };
  }, [assessment, stats, mplr, ccfAssessments, ccfOptions]);

  // Pass scores to parent component for MPL Comparison
  useEffect(() => {
    if (onScoresChange && scores) {
      onScoresChange({
        mplaValue: scores.mplaValue,
        mplr: scores.mplr,
        mplPassFail: scores.mplPassFail,
      });
    }
  }, [scores, onScoresChange]);




  return (
    <>
    {isLoading && (
      <div style={{padding: '1rem', background: '#f0f0f0', marginBottom: '1rem'}}>
        Loading Functional Safety Assessment...
      </div>
    )}

    <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>

      {/* Scores Display Section */}
      {scores && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          padding: '1rem',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          border: '1px solid #ddd',
        }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 'bold' }}>Assessment Scores</h3>
          
          {/* MTTF Score */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ fontWeight: 'bold', fontSize: '0.95rem' }}>MTTFd Score:</span>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <span>{scores.mttf.score.toFixed(2)} years</span>
              {scores.mttf.category && (
                <span style={{ 
                  padding: '0.25rem 0.5rem', 
                  backgroundColor: '#e3f2fd', 
                  borderRadius: '4px',
                  fontSize: '0.875rem'
                }}>
                  Category: {scores.mttf.category}
                </span>
              )}
            </div>
          </div>

          {/* DCavg/HSR Score */}
  { details?.category &&( showDcAvg.includes(details?.category) || scores.dcavg.isFromHSR )  &&       <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            {scores.dcavg.isFromHSR ? (
              <>
                <span style={{ fontWeight: 'bold', fontSize: '0.95rem' }}>HSR Score:</span>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span>{scores.dcavg.hsrScore}</span>
                  <span style={{ color: '#666' }}>→</span>
                  <span>DCavg Equivalent: {scores.dcavg.score.toFixed(2)}%</span>
                  {scores.dcavg.category && (
                    <span style={{ 
                      padding: '0.25rem 0.5rem', 
                      backgroundColor: '#e8f5e9', 
                      borderRadius: '4px',
                      fontSize: '0.875rem'
                    }}>
                      Category: {scores.dcavg.category}
                    </span>
                  )}
                </div>
              </>
            ) : (
              <>
                <span style={{ fontWeight: 'bold', fontSize: '0.95rem' }}>DCavg Score:</span>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <span>{scores.dcavg.score.toFixed(2)}%</span>
                  {scores.dcavg.category && (
                    <span style={{ 
                      padding: '0.25rem 0.5rem', 
                      backgroundColor: '#e8f5e9', 
                      borderRadius: '4px',
                      fontSize: '0.875rem'
                    }}>
                      Category: {scores.dcavg.category}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>}

          {/* CCF Score */}
          {details?.category && showCcf.includes(details?.category) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <span style={{ fontWeight: 'bold', fontSize: '0.95rem' }}>CCF Score:</span>
              
              {/* Overall CCF Score */}
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                <span style={{ fontWeight: 'bold' }}>Overall:</span>
                <span>{scores.ccf.score}</span>
                {scores.ccf.result && scores.ccf.result !== 'N/A' && (
                  <span style={{ 
                    padding: '0.25rem 0.5rem', 
                    backgroundColor: scores.ccf.result === 'PASS' ? '#e8f5e9' : '#ffebee',
                    color: scores.ccf.result === 'PASS' ? '#2e7d32' : '#c62828',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                    border: `1px solid ${scores.ccf.result === 'PASS' ? '#4caf50' : '#f44336'}`
                  }}>
                    {scores.ccf.result === 'FAILED' ? 'FAILED' : scores.ccf.result}
                  </span>
                )}
              </div>

              {/* Per-Section CCF Results */}
              {scores.ccf.byType && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginTop: '0.5rem' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#666', marginBottom: '0.25rem' }}>
                    By Section:
                  </span>
                  
                  {/* Input Section */}
                  {(scores.ccf.byType.input.hasAssessments || scores.ccf.byType.input.isMissing || scores.ccf.byType.input.isNotApplicable) && (
                    <div style={{ 
                      display: 'flex', 
                      gap: '0.75rem', 
                      alignItems: 'center',
                      padding: '0.4rem 0.6rem',
                      backgroundColor: scores.ccf.byType.input.isNotApplicable ? '#f5f5f5' : scores.ccf.byType.input.result === 'PASS' ? '#e8f5e9' : scores.ccf.byType.input.result === 'FAIL' || scores.ccf.byType.input.result === 'FAILED' ? '#ffebee' : '#f5f5f5',
                      borderRadius: '4px',
                      border: `1px solid ${scores.ccf.byType.input.isNotApplicable ? '#9e9e9e' : scores.ccf.byType.input.result === 'PASS' ? '#4caf50' : scores.ccf.byType.input.result === 'FAIL' || scores.ccf.byType.input.result === 'FAILED' ? '#f44336' : '#9e9e9e'}`
                    }}>
                      <span style={{ fontWeight: '600', fontSize: '0.85rem', minWidth: '60px' }}>Input:</span>
                      <span style={{ fontSize: '0.85rem' }}>
                        {scores.ccf.byType.input.isNotApplicable ? 'Not Applicable' : scores.ccf.byType.input.isMissing ? 'No Data' : `Score: ${scores.ccf.byType.input.score}`}
                      </span>
                      <span style={{ 
                        padding: '0.2rem 0.4rem', 
                        backgroundColor: scores.ccf.byType.input.isNotApplicable ? '#9e9e9e' : scores.ccf.byType.input.result === 'PASS' ? '#4caf50' : scores.ccf.byType.input.result === 'FAIL' || scores.ccf.byType.input.result === 'FAILED' ? '#f44336' : '#9e9e9e',
                        color: 'white',
                        borderRadius: '3px',
                        fontSize: '0.75rem',
                        fontWeight: 'bold'
                      }}>
                        {scores.ccf.byType.input.isNotApplicable ? 'N/A' : scores.ccf.byType.input.isMissing ? 'FAILED (Missing)' : scores.ccf.byType.input.result}
                      </span>
                    </div>
                  )}

                  {/* Logic Section */}
                  {(scores.ccf.byType.logic.hasAssessments || scores.ccf.byType.logic.isMissing || scores.ccf.byType.logic.isNotApplicable) && (
                    <div style={{ 
                      display: 'flex', 
                      gap: '0.75rem', 
                      alignItems: 'center',
                      padding: '0.4rem 0.6rem',
                      backgroundColor: scores.ccf.byType.logic.isNotApplicable ? '#f5f5f5' : scores.ccf.byType.logic.result === 'PASS' ? '#e8f5e9' : scores.ccf.byType.logic.result === 'FAIL' || scores.ccf.byType.logic.result === 'FAILED' ? '#ffebee' : '#f5f5f5',
                      borderRadius: '4px',
                      border: `1px solid ${scores.ccf.byType.logic.isNotApplicable ? '#9e9e9e' : scores.ccf.byType.logic.result === 'PASS' ? '#4caf50' : scores.ccf.byType.logic.result === 'FAIL' || scores.ccf.byType.logic.result === 'FAILED' ? '#f44336' : '#9e9e9e'}`
                    }}>
                      <span style={{ fontWeight: '600', fontSize: '0.85rem', minWidth: '60px' }}>Logic:</span>
                      <span style={{ fontSize: '0.85rem' }}>
                        {scores.ccf.byType.logic.isNotApplicable ? 'Not Applicable' : scores.ccf.byType.logic.isMissing ? 'No Data' : `Score: ${scores.ccf.byType.logic.score}`}
                      </span>
                      <span style={{ 
                        padding: '0.2rem 0.4rem', 
                        backgroundColor: scores.ccf.byType.logic.isNotApplicable ? '#9e9e9e' : scores.ccf.byType.logic.result === 'PASS' ? '#4caf50' : scores.ccf.byType.logic.result === 'FAIL' || scores.ccf.byType.logic.result === 'FAILED' ? '#f44336' : '#9e9e9e',
                        color: 'white',
                        borderRadius: '3px',
                        fontSize: '0.75rem',
                        fontWeight: 'bold'
                      }}>
                        {scores.ccf.byType.logic.isNotApplicable ? 'N/A' : scores.ccf.byType.logic.isMissing ? 'FAILED (Missing)' : scores.ccf.byType.logic.result}
                      </span>
                    </div>
                  )}

                  {/* Output Section */}
                  {(scores.ccf.byType.output.hasAssessments || scores.ccf.byType.output.isMissing || scores.ccf.byType.output.isNotApplicable) && (
                    <div style={{ 
                      display: 'flex', 
                      gap: '0.75rem', 
                      alignItems: 'center',
                      padding: '0.4rem 0.6rem',
                      backgroundColor: scores.ccf.byType.output.isNotApplicable ? '#f5f5f5' : scores.ccf.byType.output.result === 'PASS' ? '#e8f5e9' : scores.ccf.byType.output.result === 'FAIL' || scores.ccf.byType.output.result === 'FAILED' ? '#ffebee' : '#f5f5f5',
                      borderRadius: '4px',
                      border: `1px solid ${scores.ccf.byType.output.isNotApplicable ? '#9e9e9e' : scores.ccf.byType.output.result === 'PASS' ? '#4caf50' : scores.ccf.byType.output.result === 'FAIL' || scores.ccf.byType.output.result === 'FAILED' ? '#f44336' : '#9e9e9e'}`
                    }}>
                      <span style={{ fontWeight: '600', fontSize: '0.85rem', minWidth: '60px' }}>Output:</span>
                      <span style={{ fontSize: '0.85rem' }}>
                        {scores.ccf.byType.output.isNotApplicable ? 'Not Applicable' : scores.ccf.byType.output.isMissing ? 'No Data' : `Score: ${scores.ccf.byType.output.score}`}
                      </span>
                      <span style={{ 
                        padding: '0.2rem 0.4rem', 
                        backgroundColor: scores.ccf.byType.output.isNotApplicable ? '#9e9e9e' : scores.ccf.byType.output.result === 'PASS' ? '#4caf50' : scores.ccf.byType.output.result === 'FAIL' || scores.ccf.byType.output.result === 'FAILED' ? '#f44336' : '#9e9e9e',
                        color: 'white',
                        borderRadius: '3px',
                        fontSize: '0.75rem',
                        fontWeight: 'bold'
                      }}>
                        {scores.ccf.byType.output.isNotApplicable ? 'N/A' : scores.ccf.byType.output.isMissing ? 'FAILED (Missing)' : scores.ccf.byType.output.result}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* MPLA Criteria Check */}
          {scores.mpla && details?.category && (() => {
            const criteriaDescriptions = getCriteriaDescription(
              details.category,
              scores.mttf.category,
              scores.dcavg.category,
              scores.ccf.result,
              scores.mpla
            );
            
            return (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '0.5rem',
                padding: '0.75rem',
                backgroundColor: scores.mpla.meetsCriteria ? '#e8f5e9' : '#ffebee',
                border: `1px solid ${scores.mpla.meetsCriteria ? '#4caf50' : '#f44336'}`,
                borderRadius: '4px',
                marginTop: '0.5rem'
              }}>
                <div style={{ 
                  fontWeight: 'bold', 
                  fontSize: '0.95rem',
                  color: scores.mpla.meetsCriteria ? '#2e7d32' : '#c62828'
                }}>
                  MPLA Criteria: {scores.mpla.meetsCriteria ? '✓ MEETS CRITERIA' : '✗ DOES NOT MEET CRITERIA'}
                </div>
                <div style={{ fontSize: '0.875rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <div>
                    <div style={{ fontWeight: '500' }}>
                      MTTF: {scores.mpla.details.mttf ? '✓' : '✗'} {scores.mttf.category || 'N/A'}
                    </div>
                    {!scores.mpla.details.mttf && criteriaDescriptions.mttf && (
                      <div style={{ 
                        fontSize: '0.8rem', 
                        color: '#c62828', 
                        marginLeft: '1.5rem', 
                        marginTop: '0.25rem',
                        fontStyle: 'italic'
                      }}>
                        {criteriaDescriptions.mttf}
                      </div>
                    )}
                  </div>
                  <div>
                    <div style={{ fontWeight: '500' }}>
                      DCavg: {scores.mpla.applicable.dc 
                        ? `${scores.mpla.details.dc ? '✓' : '✗'} ${scores.dcavg.category || 'N/A'}` 
                        : 'N/A'}
                    </div>
                    {scores.mpla.applicable.dc && !scores.mpla.details.dc && criteriaDescriptions.dc && (
                      <div style={{ 
                        fontSize: '0.8rem', 
                        color: '#c62828', 
                        marginLeft: '1.5rem', 
                        marginTop: '0.25rem',
                        fontStyle: 'italic'
                      }}>
                        {criteriaDescriptions.dc}
                      </div>
                    )}
                  </div>
                  <div>
                    <div style={{ fontWeight: '500' }}>
                      CCF: {scores.mpla.applicable.ccf 
                        ? `${scores.mpla.details.ccf ? '✓' : '✗'} ${scores.ccf.result || 'N/A'}` 
                        : 'N/A'}
                    </div>
                    {scores.mpla.applicable.ccf && !scores.mpla.details.ccf && criteriaDescriptions.ccf && (
                      <div style={{ 
                        fontSize: '0.8rem', 
                        color: '#c62828', 
                        marginLeft: '1.5rem', 
                        marginTop: '0.25rem',
                        fontStyle: 'italic'
                      }}>
                        {criteriaDescriptions.ccf}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* MPLa (Machine Performance Level Achieved) */}
          {scores.mplaValue && details?.category && (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '0.5rem',
              padding: '0.75rem',
              backgroundColor: '#e3f2fd',
              border: '1px solid #2196f3',
              borderRadius: '4px',
              marginTop: '0.5rem'
            }}>
              <div style={{ 
                fontWeight: 'bold', 
                fontSize: '0.95rem',
                color: '#1976d2'
              }}>
                MPLa (Machine Performance Level Achieved): {scores.mplaValue}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Notification when MTTFd is HIGH suggesting Category 1 */}
      {/* {scores && scores.mttf.category === MTTF_CATEGORY.HIGH && details?.category !== FUNCTIONAL_SAFETY_CATEGORY.CATEGORY_1 && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '8px',
          color: '#856404',
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
            ⚠️ Recommendation
          </div>
          <div>
            MTTFd is HIGH ({scores.mttf.score.toFixed(2)} years). It is recommended to select <strong>Category 1</strong> for this assessment.
          </div>
        </div>
      )} */}

      <CustomSwitch
        onChange={e =>
          handleChange('hydraulicSystem', !details?.hydraulicSystem)
        }
        checked={details?.hydraulicSystem}
        label={'Is this a hydraulic control system?'}
      />
      {hydraulicError && (
        <div style={{ color: '#d32f2f', fontSize: '0.875rem' }}>{hydraulicError}</div>
      )}

      {/* <div style={{display: 'flex', gap: '1rem',alignItems:"center"}}>
        <label>Is this a hydraulic control system?</label>

        <CustomToggleButton
      value={details?.hydraulicSystem}
      handleChange={value =>
        handleChange('hydraulicSystem', value)
      }
      buttons={[
        {label:"Yes",value:true},
        {label:"No",value:false},
      ]}
      />
      </div> */}

     

      {/* <div style={{display: 'flex', gap: '12px', justifyItems: 'center'}}> */}
      {/* <span>Control System category</span> */}
      <CustomSelect
        label={'Control System category'}
        options={filteredCategory}
        value={details?.category}
        handleChange={(e) => handleChange('category', e.target.value)}
      />
      {categoryError && (
        <div style={{ color: '#d32f2f', fontSize: '0.875rem' }}>{categoryError}</div>
      )}

      {details?.category &&<img src={getMplaCategoryImage(details?.category)} alt="category" style={{width: '400px', height: '300px'}} />}

      {/* <CustomSwitch
        onChange={e => handleChange('showDcAvg', !details?.showDcAvg)}
        checked={details?.showDcAvg}
        label={'Show DCavg Assessment'}
      />

      <CustomSwitch
        onChange={e => handleChange('showCcf', !details?.showCcf)}
        checked={details?.showCcf}
        label={'Show CCF Assessment'}
      /> */}
      {/* </div> */}

      <div style={{display: 'flex', flexDirection: 'column', gap: '1.5rem'}}>
        <ModalButton
          clickHandler={() => setMtdfModal(true)}
          btnText={' Mean Time to Dangerous Failure (MTTFd) Assessment'}
          disabled={!details?.category}
        />

        {details?.category && showHsr.includes(details?.category) && details?.hydraulicSystem && (
          <CustomSwitch
            onChange={e => handleChange('hsrMethod', !details?.hsrMethod)}
            checked={details?.hsrMethod}
            label={'Use HSR method instead'}
          />
        )}

        {details?.hsrMethod && details?.category && showHsr.includes(details?.category) && details?.hydraulicSystem && (
          <ModalButton
            clickHandler={() => {
              sethsrModal(true);
            }}
            btnText={"HSR Assessment"}
          />
        )}

        {!details?.hsrMethod && details?.category && showDcAvg.includes(details?.category) && (
          <ModalButton
            clickHandler={() => {
              setDcavgModal(true);  
            }}
            btnText={'Diagnostic Coverage (DCavg) Assessment'}
          />
        )}

        {details?.category && showCcf.includes(details?.category) && (
          <ModalButton
            clickHandler={() => setccfModal(true)}
            btnText={'Common Cause Failure (CCF)'}
          />
        )}
      </div>



 {/* modals */}

      { (
        <MTTFd
          show={mtdfModal}
          setShow={setMtdfModal}
          assessment={assessment}
          groupedMTTFData={groupedMTTFData}
          createMTTF={createMTTF}
          updateMTTF={updateMTTF}
          deleteMTTF={deleteMTTF}
          isCreating={isCreating}
          isUpdating={isUpdating}
          isDeleting={isDeleting}
        />
      )}

      {dcavgModal && (
        <DCavg
          show={dcavgModal}
          setShow={setDcavgModal}
          assessment={assessment}
          groupedMTTFData={groupedMTTFData}
          dcavgAssessments={dcavgAssessments}
          hsrAssessments={hsrAssessments}
          createDCavg={createDCavg}
          updateDCavg={updateDCavg}
          deleteDCavg={deleteDCavg}
          isCreating={isCreating}
          isUpdating={isUpdating}
          isDeleting={isDeleting}
        />
      )}

      {ccfModal && (
        <CCF
          show={ccfModal}
          setShow={setccfModal}
          assessment={assessment}
          ccfOptions={ccfOptions}
          ccfAssessments={ccfAssessments}
          createCCF={createCCF}
          deleteCCF={deleteCCF}
          upsertAssessment={upsertAssessment}
          isCreating={isCreating}
          isDeleting={isDeleting}
        />
      )}

      {hsrModal && (
        <HSR
          show={hsrModal}
          setShow={sethsrModal}
          assessment={assessment}
          hsrOptions={hsrOptions}
          hsrAssessments={hsrAssessments}
          createHSR={createHSR}
          deleteHSR={deleteHSR}
          isCreating={isCreating}
          isDeleting={isDeleting}
        />
      )}
    </div>
    </>
  );
};

const ModalButton = ({btnText, clickHandler ,disabled=false}) => {
  return (
    <Button
      disabled={disabled}
      clickHandler={clickHandler}
      styles={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        maxWidth: '450px',
        padding:"1rem"
      }}
      variant="submit-button">
      {btnText}
      <OpenInNewIcon />
    </Button>
  );
};

export default EnhancedFunctionalSafety;
